import { useCallback, useEffect, useRef, useState } from 'react';
import { logger } from '../lib/logger';

export interface WakeLockStatus {
  /** False when the API is missing entirely — including any non-secure context. */
  supported: boolean;
  /** True while a lock is actually held. Observable so a silent no-op is visible. */
  active: boolean;
  /**
   * Why the lock is not held: the last rejection ("NotAllowedError: …" usually means
   * Low Power Mode or a UA that wants a user gesture) or a system-initiated release.
   * Null while held or before any attempt. Exists so on-device debugging doesn't need
   * a console.
   */
  lastError: string | null;
  /**
   * Re-request from inside a user gesture. Some WebKit builds deny requests made
   * without transient activation, so a request from an effect can fail where the
   * same request from a tap handler succeeds. No-op while a lock is already held.
   */
  retry: () => void;
}

/**
 * Holds a screen wake lock while `enabled` is true.
 *
 * Preach Mode is read from a pulpit. In scroll mode there is incidental touch every few
 * seconds, but in Book mode a preacher can sit on a single page for several minutes with
 * no input at all — long enough for an iPad to auto-lock mid-sermon in front of the
 * congregation.
 *
 * The UA releases the lock on its own whenever the page is hidden (tab switch, app
 * background, screen lock) and may release it for other reasons such as Low Power Mode,
 * so the lock has to be reacquired on every `visibilitychange`.
 *
 * NOTE: Wake Lock requires a **secure context**. Over plain http on a LAN address
 * (`npm run dev -- --host`) `navigator.wakeLock` is undefined and this hook silently
 * no-ops — device testing must use HTTPS (vite --https + mkcert, a tunnel, or a Vercel
 * preview). The unsupported path logs, so "unsupported" is distinguishable from
 * "never attempted" when debugging on-device.
 *
 * Every failure is non-fatal: the worst case is the screen dimming as it otherwise would.
 */
export function useWakeLock(enabled: boolean = true): WakeLockStatus {
  const sentinelRef = useRef<WakeLockSentinel | null>(null);
  const [active, setActive] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const acquireRef = useRef<(() => void) | null>(null);

  const supported = typeof navigator !== 'undefined' && 'wakeLock' in navigator;

  const retry = useCallback(() => acquireRef.current?.(), []);

  useEffect(() => {
    if (!enabled) return;
    if (!supported) {
      logger.log('Screen wake lock unavailable: API not present (needs HTTPS or localhost)');
      return;
    }

    // Guards against an in-flight request resolving after cleanup has run.
    let cancelled = false;

    const release = () => {
      const sentinel = sentinelRef.current;
      sentinelRef.current = null;
      setActive(false);
      if (sentinel && !sentinel.released) {
        void sentinel.release().catch(() => {
          /* already released by the UA — nothing to do */
        });
      }
    };

    const acquire = async () => {
      if (cancelled) return;
      // request() rejects unless the document is visible and active.
      if (document.visibilityState !== 'visible') return;

      try {
        const sentinel = await navigator.wakeLock.request('screen');

        if (cancelled) {
          void sentinel.release().catch(() => {});
          return;
        }

        // Drop our reference the moment the UA releases the lock, so liveness never
        // depends on reading `sentinel.released` at an arbitrary later point. Only a
        // system-initiated release reaches the body — our own release() nulls the ref
        // first — so the message distinguishes "granted then dropped" from "never granted".
        sentinel.addEventListener('release', () => {
          if (sentinelRef.current === sentinel) {
            sentinelRef.current = null;
            setActive(false);
            setLastError('released by the system');
          }
        });

        // Two acquire() calls can overlap — the visibility guard above runs *before*
        // the await, so both can pass. Release whichever landed first rather than
        // orphaning it; an orphaned sentinel would keep the screen awake for the rest
        // of the session, long after this component unmounts.
        if (sentinelRef.current && sentinelRef.current !== sentinel) release();

        sentinelRef.current = sentinel;
        setActive(true);
        setLastError(null);
      } catch (error) {
        // Thrown on low battery, or if visibility was lost mid-request. Non-fatal.
        setLastError(error instanceof Error ? `${error.name}: ${error.message}` : String(error));
        logger.warn('Screen wake lock request failed:', error);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      // Anything held across a hide is dead; the `release` event that would have
      // cleared it is not guaranteed to have been dispatched yet. Drop it explicitly
      // rather than trusting `released`, then take a fresh lock.
      release();
      void acquire();
    };

    acquireRef.current = () => {
      if (!sentinelRef.current) void acquire();
    };

    void acquire();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      acquireRef.current = null;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      release();
    };
  }, [enabled, supported]);

  return { supported, active, lastError, retry };
}
